/** Splatter — a scatter of dots around the cursor, like a spray. */
import type { CreateModule } from '../../core/module-api';
import { cfgNum } from '../../core/module-api';
import type { PathSegment, Point } from '../../core/scene';
import { defaultStyle } from '../../core/scene';
import { circlePath, distance } from '../../core/geometry';

const ICON =
  '<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="3"/><circle cx="6" cy="7" r="1.4"/><circle cx="18" cy="8" r="1.6"/><circle cx="7" cy="17" r="1.5"/><circle cx="17" cy="16" r="1.3"/></svg>';

export function createSplatter(): CreateModule {
  let pts: Point[] = [];
  let drawing = false;

  return {
    manifest: {
      id: 'splatter',
      kind: 'create',
      name: 'Splatter',
      apiVersion: 1,
      description: 'A spray of dots scattered around your cursor.',
      icon: ICON,
      capabilities: ['random'],
      params: [
        { kind: 'slider', key: 'spread', label: 'Spread', min: 4, max: 80, step: 1, default: 26 },
        { kind: 'slider', key: 'density', label: 'Density', min: 1, max: 8, step: 1, default: 3 },
      ],
    },

    onPointerDown(s) {
      pts = [{ x: s.x, y: s.y }];
      drawing = true;
    },

    onPointerMove(s) {
      if (!drawing) return;
      const p = { x: s.x, y: s.y };
      const last = pts[pts.length - 1];
      if (!last || distance(last, p) >= 4) pts.push(p);
    },

    onPointerUp() {
      drawing = false;
    },

    getLiveShapes(ctx) {
      if (pts.length === 0) return [];
      const spread = cfgNum(ctx.config, 'spread', 26);
      const density = Math.round(cfgNum(ctx.config, 'density', 3));
      const segs: PathSegment[] = [];
      for (const p of pts) {
        for (let d = 0; d < density; d++) {
          const a = ctx.rng.next() * Math.PI * 2;
          const rr = Math.sqrt(ctx.rng.next()) * spread;
          const c = { x: p.x + Math.cos(a) * rr, y: p.y + Math.sin(a) * rr };
          const dr = 0.6 + ctx.rng.next() * 2.6;
          for (const seg of circlePath(c, dr).segments) segs.push(seg);
        }
      }
      const style = defaultStyle();
      style.mode = 'fill';
      style.color = { ...ctx.ink };
      return [ctx.newShape({ path: { segments: segs }, style })];
    },

    onClear() {
      pts = [];
    },

    onCommit() {
      pts = [];
    },
  };
}
